import {
  AgGridManager,
  DataCardinalities,
  DataTypes,
  GridColumnData,
  GridFilterTypes,
  GridRenderSlots,
  MemoisedSchemaClient,
  prepareEquipmentSchema
} from "@digitallab/grid-common-lib";
import { makeGraphqlClient } from "./graphqlClient";
import { convertToSnakeCase } from "../../../utils/helpers/converters";

/**
 * The per-field flag lists the app used to maintain by hand in `constants.js`, derived from the
 * schema instead. Each corresponds to one exported constant; the names here are the constants'
 * names minus the `listOf` prefix.
 */
export interface GridConstants {
  fieldsNotSortable: string[];
  dateRequired: string[];
  booleanRequired: string[];
  checkBoxFilters: string[];
  numberFilter: string[];
  fieldsNoFilter: string[];
  filterBlankRequired: string[];
  fieldAcceptOnlyInteger: string[];
  fieldsIgnoredForSearch: string[];
  /** Metric field names plus their imperial siblings — the app's `conversionFields`. */
  conversionFields: string[];
}

export const deriveGridConstants = (columns: GridColumnData[]): GridConstants => ({
  fieldsNotSortable: columns.filter((column) => !column.sortStatus).map((column) => column.key),
  dateRequired: columns
    .filter((column) => column.filter === GridFilterTypes.DATE)
    .map((column) => column.key),
  // Yes/no columns, which get a two-value set filter rather than the master-data one.
  booleanRequired: columns
    .filter((column) => column.renderSlot === GridRenderSlots.BOOLEAN)
    .map((column) => column.key),
  // Set filters excluding the booleans, which the app handles on their own branch.
  checkBoxFilters: columns
    .filter(
      (column) =>
        column.filter === GridFilterTypes.SET && column.renderSlot !== GridRenderSlots.BOOLEAN
    )
    .map((column) => column.key),
  numberFilter: columns
    .filter((column) => column.filter === GridFilterTypes.NUMBER)
    .map((column) => column.key),
  fieldsNoFilter: columns
    .filter((column) => column.filter === GridFilterTypes.NONE)
    .map((column) => column.key),
  filterBlankRequired: columns.filter((column) => column.filterBlank).map((column) => column.key),
  fieldAcceptOnlyInteger: columns.filter((column) => column.integerOnly).map((column) => column.key),
  fieldsIgnoredForSearch: columns
    .filter((column) => column.ignoredForSearch)
    .map((column) => column.key),
  // Metric keys only, matching the hand-written list. The imperial siblings are reachable via
  // getImperialFields(); including them here would change `MasterDataContainer`'s forEach over
  // this list from 9 iterations to 18.
  conversionFields: columns.filter((column) => column.imperialField).map((column) => column.key)
});

/**
 * Schema-derived grid column data, cached for the session.
 *
 * The equipment grid's column list used to be the hand-written `allTableColumn()` array in
 * `constants.js`. It now comes from the schema's `grid` aspect, so a field's column
 * behaviour lives beside the rest of its definition.
 *
 * Why a module-level cache rather than a hook: `allTableColumn()` is synchronous and is read
 * from places that are not components — `exportSearchHelper`, and cell renderers in
 * `addEquipmentService` that resolve the metric/imperial sibling per cell. Preparing the
 * schema is asynchronous (it fetches master data for the set-filter options), so the result
 * is resolved once via {@link loadEquipmentGridColumns} and read synchronously thereafter.
 *
 * `cache-first` is deliberate: every caller wants the same master data, and the columns are
 * derived once per session.
 */
let columnDataCache: GridColumnData[] | null = null;
let imperialFieldCache: Record<string, string> = {};
let gridConstantsCache: GridConstants | null = null;
let equipmentArrayFieldsCache: string[] | null = null;
let inFlight: Promise<GridColumnData[]> | null = null;

/**
 * Prepare the equipment schema and cache the derived column data.
 *
 * Safe to call repeatedly — concurrent callers share one in-flight request, and later calls
 * resolve from the cache. Call this before the equipment grid renders.
 */
export const loadEquipmentGridColumns = async (): Promise<GridColumnData[]> => {
  if (columnDataCache) {
    return columnDataCache;
  }
  if (inFlight) {
    return inFlight;
  }

  // Memoised so the four category/group/person reads behind the set-filter options happen
  // once each, rather than once per field that asks for them.
  const client = new MemoisedSchemaClient(makeGraphqlClient("cache-first"));

  inFlight = prepareEquipmentSchema(client)
    .then(async (schema) => {
      const manager = new AgGridManager(schema);
      // Resolves the grid.filterValues getters — the set-filter options for the columns the
      // form drives through a picker or free text, which are therefore not data enums.
      // Nothing was fetched for them until this call.
      await manager.init();

      const columns = manager.generateColumnData();
      equipmentArrayFieldsCache = Object.entries(schema)
        .filter(([, field]) => {
          const data = field?.data;
          if (!data) {
            return false;
          }
          return data.cardinality === DataCardinalities.MULTIPLE || data.type === DataTypes.ARRAY;
        })
        .map(([name]) => convertToSnakeCase(name));
      columnDataCache = columns;
      imperialFieldCache = manager.getImperialFieldMap();
      gridConstantsCache = deriveGridConstants(columns);
      return columns;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/**
 * Cached column data, or `null` when {@link loadEquipmentGridColumns} has not resolved yet.
 *
 * Callers that can await should use {@link loadEquipmentGridColumns} instead; this exists for
 * the synchronous readers.
 */
export const getEquipmentGridColumns = (): GridColumnData[] | null => columnDataCache;

/**
 * Metric field name → imperial sibling field name, for the dimension columns.
 *
 * Read on the cell-render path, which cannot await. Empty until the schema has loaded; by the
 * time cells render, the grid has already loaded its columns.
 */
export const getImperialFields = (): Record<string, string> => imperialFieldCache;

/**
 * The schema-derived flag lists, or `null` before {@link loadEquipmentGridColumns} resolves.
 *
 * The `listOf…` constants in `constants.js` read this and fall back to their hand-written
 * originals while it is null, so a screen that renders before the schema loads keeps the old
 * behaviour rather than seeing empty lists.
 */
export const getGridConstants = (): GridConstants | null => gridConstantsCache;

/**
 * Schema-derived field names that are logically list-typed (multiple cardinality / array type).
 * Null before the schema has been loaded.
 */
export const getEquipmentArrayFields = (): string[] | null => equipmentArrayFieldsCache;

/** Test seam: drop the cached schema so each test starts from a known state. */
export const resetEquipmentGridColumns = (): void => {
  columnDataCache = null;
  imperialFieldCache = {};
  gridConstantsCache = null;
  equipmentArrayFieldsCache = null;
  inFlight = null;
};
